import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import {
  DataElementView,
  SourceFieldMappingView,
  DataSource,
  ElementVersion,
  ReferenceList,
  ProcessingRule,
  ElementVersionProcessingRule
} from "@/entities/all";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Blocks,
  Box,
  Database,
  GitBranch,
  List,
  Puzzle,
  FileQuestion,
  CheckCircle,
  AlertTriangle,
  BookOpen,
  Code,
  RefreshCw,
  BarChart3,
  Link as LinkIcon,
  FileX,
  Server
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

function getScore(pct) {
  if (pct >= 90) return { color: 'var(--pill-green-strong-color)', bg: 'var(--pill-green-bg)' };
  if (pct >= 70) return { color: 'var(--pill-amber-color)',        bg: 'var(--pill-amber-bg)' };
  return             { color: 'var(--pill-red-color)',             bg: 'var(--pill-red-bg)'   };
}

function ProgressBar({ value, color }) {
  const fillColor = color || getScore(value).color;
  return (
    <div className="w-full h-2.5 bg-[var(--table-stripe)] rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${value}%`, backgroundColor: fillColor }}
      />
    </div>
  );
}

function StatRow({ label, value, total, color }) {
  const pct = total === 0 ? 0 : Math.round((value / total) * 100);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between items-center">
        <span className="text-sm text-page-muted">{label}</span>
        <span className="text-sm font-medium" style={{ color: 'var(--page-text)' }}>
          {value} / {total} ({pct}%)
        </span>
      </div>
      <ProgressBar value={pct} color={color} />
    </div>
  );
}

function SectionCard({ icon: Icon, iconColor, title, children }) {
  return (
    <div className="bg-card-bg rounded-[8px] overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-[var(--card-border)]">
        <Icon size={18} color={iconColor} className="shrink-0" />
        <h3 className="text-sm font-semibold text-page-text">{title}</h3>
      </div>
      <div style={{ padding: '12px 20px' }}>{children}</div>
    </div>
  );
}

function Pill({ bg, color, children }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 h-[22px] rounded-[11px] text-xs font-medium shrink-0"
      style={{ backgroundColor: bg, color }}
    >
      {children}
    </span>
  );
}

const STATUS_PILL = {
  APPROVED:       { bg: 'var(--pill-green-bg)',   color: 'var(--pill-green-color)' },
  DRAFT:          { bg: 'var(--pill-blue-bg)',     color: 'var(--pill-blue-color)' },
  DEPRECATED:     { bg: 'var(--pill-red-bg)',      color: 'var(--pill-red-color)' },
  PENDING_REVIEW: { bg: 'var(--pill-amber-bg)',    color: 'var(--pill-amber-color)' },
};

const TABLE_TH = "text-left px-4 py-3 text-xs font-semibold text-page-muted uppercase tracking-wide";

export default function DataCatalogQuality() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    elements: { total: 0, withDefinition: 0, approved: 0, draft: 0, deprecated: 0 },
    mappings: { total: 0, mapped: 0, unmapped: 0, bySource: [] },
    referenceLists: { total: 0, used: 0, unused: 0 },
    processingRules: { total: 0, used: 0, unused: 0 },
    sources: { total: 0 }
  });
  const [unmappedFields, setUnmappedFields] = useState([]);
  const [elementsWithoutMappings, setElementsWithoutMappings] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => { loadCatalogStats(); }, []);

  const loadCatalogStats = async () => {
    setIsLoading(true);
    try {
      const [
        elements,
        elementVersions,
        mappingsView,
        sources,
        referenceLists,
        processingRules,
        versionRules
      ] = await Promise.all([
        DataElementView.list('canonical_name'),
        ElementVersion.list('element_id'),
        SourceFieldMappingView.list('source_name'),
        DataSource.list('source_name'),
        ReferenceList.list('list_name'),
        ProcessingRule.list('rule_name'),
        ElementVersionProcessingRule.list('element_version_id')
      ]);

      const elementsWithDefinition = elements.filter(e => e.definition && e.definition.trim() !== '');
      const approvedElements = elements.filter(e => e.status === 'APPROVED');
      const draftElements = elements.filter(e => e.status === 'DRAFT');
      const deprecatedElements = elements.filter(e => e.status === 'DEPRECATED');

      const mappedFields = mappingsView.filter(m => m.canonical_element_id);
      const unmappedFieldsList = mappingsView.filter(m => !m.canonical_element_id);

      const mappedElementIds = new Set(mappedFields.map(m => String(m.canonical_element_id)));
      const elementsNotMapped = elements.filter(e => !mappedElementIds.has(String(e.id)));

      const mappingsBySource = {};
      mappingsView.forEach(m => {
        const sourceName = m.source_name || 'Unknown';
        if (!mappingsBySource[sourceName]) {
          mappingsBySource[sourceName] = { total: 0, mapped: 0, unmapped: 0, sourceId: m.source_id };
        }
        mappingsBySource[sourceName].total++;
        if (m.canonical_element_id) mappingsBySource[sourceName].mapped++;
        else mappingsBySource[sourceName].unmapped++;
      });

      const usedListIds = new Set(
        elementVersions.filter(v => v.reference_list_id).map(v => String(v.reference_list_id))
      );
      const usedRuleIds = new Set(versionRules.map(vr => String(vr.processing_rule_id)));

      setStats({
        elements: {
          total: elements.length,
          withDefinition: elementsWithDefinition.length,
          approved: approvedElements.length,
          draft: draftElements.length,
          deprecated: deprecatedElements.length
        },
        mappings: {
          total: mappingsView.length,
          mapped: mappedFields.length,
          unmapped: unmappedFieldsList.length,
          bySource: Object.entries(mappingsBySource)
            .map(([name, data]) => ({ name, ...data }))
            .sort((a, b) => b.unmapped - a.unmapped)
        },
        referenceLists: {
          total: referenceLists.length,
          used: referenceLists.filter(l => usedListIds.has(String(l.id))).length,
          unused: referenceLists.filter(l => !usedListIds.has(String(l.id))).length
        },
        processingRules: {
          total: processingRules.length,
          used: processingRules.filter(r => usedRuleIds.has(String(r.id))).length,
          unused: processingRules.filter(r => !usedRuleIds.has(String(r.id))).length
        },
        sources: { total: sources.length }
      });
      setUnmappedFields(unmappedFieldsList);
      setElementsWithoutMappings(elementsNotMapped);
    } catch (error) {
      console.error('Error loading catalog stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPercentage = (value, total) => total === 0 ? 0 : Math.round((value / total) * 100);

  const overallScore = (() => {
    if (stats.elements.total === 0) return 0;
    return Math.round((
      getPercentage(stats.elements.withDefinition, stats.elements.total) +
      getPercentage(stats.elements.approved, stats.elements.total) +
      getPercentage(stats.elements.total - elementsWithoutMappings.length, stats.elements.total) +
      getPercentage(stats.mappings.mapped, stats.mappings.total)
    ) / 4);
  })();

  const scoreColor = '#4DB300';

  const TABS = [
    { id: 'overview',          label: 'Coverage Overview',                               icon: BarChart3 },
    { id: 'unmapped',          label: `Unmapped Fields (${stats.mappings.unmapped})`,    icon: FileQuestion },
    { id: 'elements-unmapped', label: `No Mappings (${elementsWithoutMappings.length})`, icon: FileX },
    { id: 'by-source',         label: 'By Source',                                      icon: Server },
  ];

  const STAT_CARDS = [
    { icon: Blocks,    color: 'rgba(122, 197, 255, 1)', label: 'Data Elements',    value: stats.elements.total },
    { icon: Box,       color: 'rgba(182, 129, 235, 1)', label: 'Data Sources',     value: stats.sources.total },
    { icon: GitBranch, color: 'rgba(144, 178, 245, 1)', label: 'Source Mappings',  value: stats.mappings.total },
    { icon: List,      color: 'rgba(147, 209, 77, 1)',  label: 'Reference Lists',  value: stats.referenceLists.total },
    { icon: Puzzle,    color: 'rgba(242, 182, 129, 1)', label: 'Processing Rules', value: stats.processingRules.total },
  ];

  return (
    <div data-tour="page-dictionary-quality" className="pt-5 pb-24 px-4 md:py-[30px] md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Dictionary Quality</h1>
          <p className="text-sm font-normal text-page-muted">
            Monitor coverage, usage, and completeness of your data catalog
          </p>
        </div>
        <button
          onClick={loadCatalogStats}
          className="flex items-center gap-[6px] w-[183px] h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
        >
          <RefreshCw size={16} strokeWidth={2.5} />
          Refresh Stats
        </button>
      </div>

      {/* Overall Score */}
      <div className="bg-card-bg rounded-[8px] p-3">
        <div className="flex flex-col gap-[10px]">
          {/* Top: percentage + title/description */}
          <div className="flex items-center" style={{ gap: '20px' }}>
            {isLoading ? (
              <Skeleton className="h-[50px] w-[80px] shrink-0" />
            ) : (
              <span
                className="shrink-0 leading-none"
                style={{ color: scoreColor, fontSize: '50px', fontWeight: 500 }}
              >
                {overallScore}%
              </span>
            )}
            <div className="flex flex-col" style={{ gap: '8px' }}>
              <span style={{ color: 'var(--page-text)', fontSize: '22px', fontWeight: 500, lineHeight: 1.2 }}>
                Overall Catalog Quality
              </span>
              <span style={{ color: 'var(--page-muted)', fontSize: '14px', fontWeight: 400, lineHeight: 1.4 }}>
                Based on documentation, approval &amp; mapping coverage
              </span>
            </div>
          </div>
          {/* Bottom: progress bar, max 631px */}
          <div className="h-2.5 rounded-full overflow-hidden max-w-[631px]" style={{ backgroundColor: 'var(--page-bg)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${overallScore}%`, backgroundColor: scoreColor }}
            />
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid gap-[10px] grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
        {STAT_CARDS.map(({ icon: Icon, color, label, value }) => (
          <div
            key={label}
            className="bg-card-bg rounded-[8px] flex items-start gap-3"
            style={{ padding: '12px 20px' }}
          >
            <Icon size={24} color={color} className="shrink-0" />
            <div className="flex flex-col" style={{ gap: '6px' }}>
              <span style={{ color: 'var(--page-text)', fontSize: '14px', fontWeight: 500 }}>{label}</span>
              {isLoading
                ? <Skeleton className="h-[30px] w-12" />
                : <span style={{ color: 'var(--page-text)', fontSize: '30px', fontWeight: 500, lineHeight: 'normal' }}>{value}</span>
              }
            </div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex w-full border-2 border-[#377AD6] rounded-[5px] overflow-hidden">
        {TABS.map(({ id, label, icon: Icon }, idx) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={[
              'flex-1 flex items-center justify-center gap-1.5 h-[30px] text-xs font-medium cursor-pointer border-0 outline-none transition-colors',
              idx < TABS.length - 1 ? '[border-right:2px_solid_#377AD6]' : '',
              activeTab === id ? 'bg-[#377AD6] text-white' : 'bg-card-bg text-[#377AD6] hover:bg-[#377AD6]/5'
            ].filter(Boolean).join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Coverage Overview */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-[10px]">
          <SectionCard icon={Blocks} iconColor="rgba(122, 197, 255, 1)" title="Element Documentation">
            <div className="flex flex-col gap-4">
              <StatRow label="With Definition" value={stats.elements.withDefinition} total={stats.elements.total} color="#F49885" />
              <StatRow label="Approved Status" value={stats.elements.approved} total={stats.elements.total} />
              <div className="pt-3 border-t border-[var(--card-border)] flex flex-wrap gap-2">
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Approved: {stats.elements.approved}</Pill>
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Draft: {stats.elements.draft}</Pill>
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Deprecated: {stats.elements.deprecated}</Pill>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={GitBranch} iconColor="rgba(144, 178, 245, 1)" title="Mapping Coverage">
            <div className="flex flex-col gap-4">
              <StatRow label="Source Fields Mapped" value={stats.mappings.mapped} total={stats.mappings.total} color="#4DB300" />
              <StatRow
                label="Elements With Mappings"
                value={stats.elements.total - elementsWithoutMappings.length}
                total={stats.elements.total}
                color="#FFC978"
              />
              <div className="pt-3 border-t border-[var(--card-border)] flex flex-wrap gap-2">
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Mapped: {stats.mappings.mapped}</Pill>
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Unmapped: {stats.mappings.unmapped}</Pill>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={List} iconColor="rgba(147, 209, 77, 1)" title="Reference Lists Usage">
            <div className="flex flex-col gap-4">
              <StatRow label="Lists In Use" value={stats.referenceLists.used} total={stats.referenceLists.total} color="#4DB300" />
              <div className="pt-3 border-t border-[var(--card-border)] flex flex-wrap gap-2">
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Used: {stats.referenceLists.used}</Pill>
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Unused: {stats.referenceLists.unused}</Pill>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={Puzzle} iconColor="rgba(242, 182, 129, 1)" title="Processing Rules Usage">
            <div className="flex flex-col gap-4">
              <StatRow label="Rules In Use" value={stats.processingRules.used} total={stats.processingRules.total} color="#F49885" />
              <div className="pt-3 border-t border-[var(--card-border)] flex flex-wrap gap-2">
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Used: {stats.processingRules.used}</Pill>
                <Pill bg="var(--table-header-bg)" color="var(--table-header-color)">Unused: {stats.processingRules.unused}</Pill>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* Unmapped Source Fields */}
      {activeTab === 'unmapped' && (
        <SectionCard icon={FileQuestion} iconColor="var(--pill-red-color)" title="Unmapped Source Fields">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : unmappedFields.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <CheckCircle size={40} color="var(--pill-green-color)" />
              <p className="text-sm text-page-muted">All source fields are mapped to data elements!</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 -mb-5">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--table-header-bg)]">
                    <th className={TABLE_TH}>Source</th>
                    <th className={TABLE_TH}>Field Name</th>
                    <th className={TABLE_TH}>Details</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {unmappedFields.slice(0, 50).map((field, i) => (
                    <tr key={field.id} className={`border-b border-[var(--card-border)] ${i % 2 === 1 ? 'bg-[var(--table-stripe)]' : 'bg-card-bg'}`}>
                      <td className="px-4 py-3">
                        <Pill bg="var(--pill-blue-bg)" color="var(--pill-blue-color)">
                          {field.source_name || 'Unknown'}
                        </Pill>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-page-text">{field.source_field_name}</td>
                      <td className="px-4 py-3 text-sm text-page-muted max-w-[240px] truncate">{field.source_field_details || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(createPageUrl(`source-mappings/${field.id}`))}
                          className="h-7 px-3 rounded-[5px] border border-[var(--card-border)] text-xs font-medium text-page-muted hover:border-nav-active hover:text-nav-active transition-colors cursor-pointer"
                        >
                          View / Map
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {unmappedFields.length > 50 && (
                <p className="text-sm text-page-muted text-center py-4">
                  Showing 50 of {unmappedFields.length} unmapped fields
                </p>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* Elements Without Mappings */}
      {activeTab === 'elements-unmapped' && (
        <SectionCard icon={FileX} iconColor="rgba(234,88,12,0.85)" title="Data Elements Without Source Mappings">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : elementsWithoutMappings.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <CheckCircle size={40} color="var(--pill-green-color)" />
              <p className="text-sm text-page-muted">All data elements have at least one source mapping!</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 -mb-5">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--table-header-bg)]">
                    <th className={TABLE_TH}>Element Name</th>
                    <th className={TABLE_TH}>Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {elementsWithoutMappings.slice(0, 50).map((element, i) => {
                    const pill = STATUS_PILL[element.status] || { bg: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-color)' };
                    return (
                      <tr key={element.id} className={`border-b border-[var(--card-border)] ${i % 2 === 1 ? 'bg-[var(--table-stripe)]' : 'bg-card-bg'}`}>
                        <td className="px-4 py-3">
                          <Link
                            to={createPageUrl(`element-details?id=${element.id}`)}
                            className="text-sm font-medium text-nav-active hover:underline"
                          >
                            {element.canonical_name}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <Pill bg={pill.bg} color={pill.color}>{element.status}</Pill>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => navigate(createPageUrl('source-mappings'), { state: { preselectedElement: element.id } })}
                            className="h-7 px-3 rounded-[5px] border border-[var(--card-border)] text-xs font-medium text-page-muted hover:border-nav-active hover:text-nav-active transition-colors cursor-pointer"
                          >
                            Add Mapping
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {elementsWithoutMappings.length > 50 && (
                <p className="text-sm text-page-muted text-center py-4">
                  Showing 50 of {elementsWithoutMappings.length} elements without mappings
                </p>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* By Source */}
      {activeTab === 'by-source' && (
        <SectionCard icon={Server} iconColor="rgba(109,40,217,0.85)" title="Mapping Coverage by Data Source">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : stats.mappings.bySource.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <p className="text-sm text-page-muted">No source mappings found</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 -mb-5">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--table-header-bg)]">
                    <th className={TABLE_TH}>Data Source</th>
                    <th className={TABLE_TH}>Total Fields</th>
                    <th className={TABLE_TH}>Mapped</th>
                    <th className={TABLE_TH}>Unmapped</th>
                    <th className={TABLE_TH}>Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.mappings.bySource.map((source, i) => {
                    const coverage = getPercentage(source.mapped, source.total);
                    const score = getScore(coverage);
                    return (
                      <tr key={source.name} className={`border-b border-[var(--card-border)] ${i % 2 === 1 ? 'bg-[var(--table-stripe)]' : 'bg-card-bg'}`}>
                        <td className="px-4 py-3">
                          <Link
                            to={createPageUrl(`data-sources/${source.sourceId}`)}
                            className="text-sm font-medium text-nav-active hover:underline"
                          >
                            {source.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-page-text">{source.total}</td>
                        <td className="px-4 py-3">
                          <Pill bg="var(--pill-green-bg)" color="var(--pill-green-color)">{source.mapped}</Pill>
                        </td>
                        <td className="px-4 py-3">
                          {source.unmapped > 0
                            ? <Pill bg="var(--pill-red-bg)" color="var(--pill-red-color)">{source.unmapped}</Pill>
                            : <Pill bg="var(--pill-neutral-bg)" color="var(--pill-neutral-color)">0</Pill>
                          }
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-2 bg-[var(--table-stripe)] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${coverage}%`, backgroundColor: score.color }} />
                            </div>
                            <span className="text-sm font-medium tabular-nums" style={{ color: score.color }}>
                              {coverage}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}

    </div>
  );
}
